// Shared types for the Broker Hub scene + its dialogs.

export interface HubSite {
  id: number;
  site_key: string;
  name: string;
  base_url: string;
  platform: string;
  receiver_status?: string;
  override_css?: string | null;
  /** 1 = Portal hat den JS-Dateikanal (sw, bg, tt, kme). ft/bex: 0 — dort kaeme nichts an. */
  js_enabled?: number;
}

export interface HubProfileCell {
  id: number;
  broker_id: number;
  site_id: number;
  status: string;
  page_url: string | null;
  page_slug: string | null;
  affiliate_url: string | null;
  card_affiliate_url: string | null;
  needs_review: number;
  has_content: number;
  updated_at: string;
  // Deploy state (from the latest successful deployment). null last_deploy_action = never
  // deployed. 'takedown' = last deploy took the page offline. changed_since_deploy = saved
  // edits not yet pushed.
  last_deploy_at?: string | null;
  last_deploy_action?: string | null;
  changed_since_deploy?: number;
  /** A broker fact this page QUOTES changed after the last deploy — stale without an edit. */
  facts_changed_since_deploy?: number;
}

export interface HubBroker {
  id: number;
  slug: string;
  name: string;
  /** Das BREITE Logo — Steckbrief und Broker-Ueberschrift. */
  logo_url: string | null;
  /**
   * Die quadratische MARKE fuer die Ranglisten-Kachel. Fehlt sie, faellt die Rangliste auf
   * `logo_url` zurueck.
   *
   * `getMatrix` liest die Katalogzeile mit `SELECT *`, das Feld kommt also ohne Zusatzabfrage mit
   * und ist im Deploy-Fenster schlicht `undefined` statt zu werfen.
   */
  logo_mark_url: string | null;
  /**
   * Zahlungsmethoden als Katalog-Kennungen (`node-app/.../zahlungsmittel.ts`), für alle Portale.
   * `null` = nicht zentral gepflegt; `[]` = bewusst keine. Der Dienst normalisiert, der rohe
   * LONGTEXT kommt hier nie an (im Deploy-Fenster: `null`).
   */
  payment_methods?: string[] | null;
  status: string;
  notes: string | null;
  /**
   * Anker der optimistischen Sperre beim Bearbeiten (`PUT /brokers/:id`).
   *
   * `getMatrix` selektiert die Katalogzeile mit `SELECT *`, der Wert kommt also ohne
   * Zusatzabfrage mit. Ist er hier veraltet, antwortet die Route 409 — genau dafuer ist er da.
   */
  updated_at: string;
  master: HubProfileCell | null;
  profiles: Record<string, HubProfileCell>;
}

export interface MatrixData {
  sites: HubSite[];
  brokers: HubBroker[];
  openDiscrepancies: number;
}

export interface Discrepancy {
  id: number;
  broker_slug: string;
  site_key: string | null;
  kind: string;
  description: string;
  evidence: string | null;
  status: string;
}

export interface FullProfile extends HubProfileCell {
  broker_slug: string;
  broker_name: string;
  // 'profile' | 'compare'. Eine Vergleichsseite hat keinen eigenen Broker (`broker_id IS NULL`)
  // und auf dem Portal kein Rueckfall-Template — der Takedown-Dialog formuliert danach.
  page_type?: string;
  site_key: string | null;
  // Portal origin (broker_hub_sites.base_url), e.g. https://www.finanztrends.de — used to resolve
  // portal-relative image paths (/wp-content/...) in editor previews against the right host.
  base_url: string | null;
  receiver_status: string | null;
  page_title: string | null;
  risk_notice: string | null;
  content_html: string | null;
  content_css: string | null;
  // Blocks editor (flag-gated): blocks document (JSON string), authoring format and the
  // optimistic-lock revision counter (server-managed, +1 per save).
  content_blocks?: string | null;
  content_format?: string | null;
  revision?: number;
  page_template: string | null;
  cms_page_id: string | null;
  imported_from: string | null;
  card_data: unknown;
  /**
   * Was die Zahlungsmethoden-Auswahl des Brokers auf die Karte schreibt (`getProfile`).
   * `null` = am Broker nicht zentral gepflegt, die Karte behält ihre eigenen `payments`.
   */
  broker_card_payments?: Array<{ src: string; alt: string }> | null;
}

export interface ProfileVersion {
  id: number;
  version: number;
  change_note: string | null;
  created_by: string | null;
  created_at: string;
  /** Format des Snapshots; null bei Ständen aus der Zeit vor dem Blocks-Editor. */
  snapshot_format?: string | null;
  /** Urteil des Servers — NICHT im Frontend nachrechnen, sonst laufen beide auseinander. */
  restorable?: boolean;
  restore_blocked_reason?: string | null;
}

export interface CatalogBroker {
  id: number;
  slug: string;
  name: string;
  logo_url: string | null;
  status: string;
}

export const STATUS_STYLES: Record<
  string,
  { label: string; color: 'success' | 'warning' | 'error' | 'default' }
> = {
  live: { label: 'Live', color: 'success' },
  draft: { label: 'Entwurf', color: 'warning' },
  taken_down: { label: 'Offline', color: 'error' },
};

// ---------- Broker-Rollout über alle Portale ----------

/** Ein Portal im Rollout eines Brokers. `kein_profil` = das Portal führt diesen Broker nicht. */
export interface RolloutPortal {
  siteId: number;
  siteKey: string;
  profileId: number | null;
  zustand: 'aktuell' | 'veraltet' | 'nie_ausgespielt' | 'karte_offen' | 'kein_profil';
  erwarteteRevision: string;
  gemeldeteRevision: string | null;
  ausgespieltAm: string | null;
  kartePlatziert: boolean | null;
}

export interface RolloutZustand {
  brokerId: number;
  /** Erst `true`, wenn JEDES Portal mit Live-Profil auf seiner aktuellen Revision steht. */
  vollstaendig: boolean;
  portale: RolloutPortal[];
}

export interface RolloutVorlaufBefund {
  siteKey: string;
  profileId: number;
  grund: string;
}

/** Was die Oberfläche je Zustand anzeigt. Ein Ton, ein Satz — keine Rohbegriffe. */
export const ROLLOUT_TEXTE: Record<
  RolloutPortal['zustand'],
  { text: string; ton: 'success' | 'warning' | 'error' | 'default' }
> = {
  aktuell: { text: 'aktuell', ton: 'success' },
  veraltet: { text: 'veraltet — Änderung noch nicht dort', ton: 'error' },
  nie_ausgespielt: { text: 'nie ausgespielt', ton: 'warning' },
  karte_offen: { text: 'Seite ja, Vergleichskarte nein', ton: 'error' },
  kein_profil: { text: 'führt diesen Broker nicht', ton: 'default' },
};

// ---- Affiliate-Links je Broker (Dialog „Affiliate-Links", Server: affiliateLinksService.ts) ----

export interface AffiliatePortalLink {
  siteId: number;
  siteKey: string;
  siteName: string;
  profileId: number | null;
  status: string | null;
  revision: number | null;
  affiliateUrl: string | null;
  pageUrl: string | null;
  aufPortal: boolean;
  ausspielZustand: RolloutPortal['zustand'] | null;
  literalLinks: Array<{ blockType: string; prop: string; url: string }>;
}

export interface AffiliateOffeneSeite {
  profileId: number;
  art: 'profil' | 'vergleich';
  siteKey: string;
  titel: string;
  pageUrl: string | null;
  revision: number;
  grund: string;
  fremdeAenderung: { wer: string; wann: string; notiz: string } | null;
  zentraleAenderungenAndererBroker: boolean;
}

export interface AffiliateLinksZustand {
  brokerId: number;
  brokerSlug: string;
  brokerName: string;
  portale: AffiliatePortalLink[];
  offeneSeiten: AffiliateOffeneSeite[];
  vergleichsseitenGesamt: number;
  laeuft: boolean;
}

export interface AffiliatePruefBefund {
  siteKey: string;
  art: 'fehler' | 'warnung' | 'info';
  text: string;
}

export interface AffiliateAusspielErgebnis {
  profileId: number;
  titel: string;
  ok: boolean;
  uebersprungen?: string;
  kartePlatziert?: boolean | null;
  error?: string;
}
